// src/components/workout/EnhancedRestTimer.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Button } from "../ui/button";
import { Play, Pause, RotateCcw } from "lucide-react";

/**
 * Enhanced Rest Timer with notification sounds
 * - Starts at 0:00 and counts up
 * - Notification sounds at percentage of target and at full rest time
 * - Continues counting up until stopped
 */
const EnhancedRestTimer = ({ exerciseId, onTimerComplete, defaultTime = 90 }) => {
  const [isActive, setIsActive] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [hasReachedFirstAlert, setHasReachedFirstAlert] = useState(false);
  const [hasReachedSecondAlert, setHasReachedSecondAlert] = useState(false);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio('/sounds/notification.mp3');
    
    // Clean up on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      clearInterval(intervalRef.current);
    };
  }, []);

  // Timer logic
  useEffect(() => {
    if (!isActive) return;

    intervalRef.current = setInterval(() => {
      setSeconds(prevSeconds => {
        const newSeconds = prevSeconds + 1;
        
        // First notification at 75% of the rest time
        const firstAlertTime = Math.floor(defaultTime * 1);
        
        // Check for notification points
        if (newSeconds === firstAlertTime && !hasReachedFirstAlert) {
          playNotification();
          setHasReachedFirstAlert(true);
        }
        
        // Second notification at full rest time
        if (newSeconds === defaultTime && !hasReachedSecondAlert) {
          playNotification();
          setHasReachedSecondAlert(true);
          
          // Notify parent component if needed
          if (onTimerComplete) {
            onTimerComplete(exerciseId);
          }
        }
        
        return newSeconds;
      });
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [isActive, hasReachedFirstAlert, hasReachedSecondAlert, exerciseId, onTimerComplete, defaultTime]);

  const playNotification = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.error('Error playing audio:', e));
    }
  };

  const startTimer = () => {
    setIsActive(true);
  };

  const pauseTimer = () => {
    setIsActive(false);
  };

  const resetTimer = () => {
    setIsActive(false);
    setSeconds(0);
    setHasReachedFirstAlert(false);
    setHasReachedSecondAlert(false);
  };

  // Format time for display (MM:SS)
  const formatTime = () => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Determine color based on rest time progress
  const getTimerColor = () => {
    if (seconds >= defaultTime) {
      return 'text-green-500'; // Full rest time reached
    } else if (seconds >= Math.floor(defaultTime * 0.5)) {
      return 'text-yellow-500'; // 75% of rest time
    }
    return 'text-foreground'; // Default color
  };

  // Show target time
  const formatTargetTime = () => {
    const mins = Math.floor(defaultTime / 60);
    const secs = defaultTime % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex justify-between items-center pt-2 border-t">
      <div>
        <p className="text-sm text-muted-foreground">Rest Timer (Target: {formatTargetTime()})</p>
        <div className={`text-2xl font-mono ${getTimerColor()}`}>
          {formatTime()}
        </div>
      </div>
      <div className="space-x-2">
        {isActive ? (
          <>
            <Button 
              variant="outline"
              onClick={pauseTimer}
            >
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </Button>
            <Button
              variant="outline"
              onClick={resetTimer}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            onClick={startTimer}
          >
            <Play className="h-4 w-4 mr-2" />
            Start
          </Button>
        )}
      </div>
    </div>
  );
};

export default EnhancedRestTimer;